import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Sparkles, Mail, Linkedin, Send } from 'lucide-react';
import * as Icons from 'lucide-react';
import { firmInfo, heroAttributes, founderProfile, practiceAreas, globalRequests } from '../data/mock';
import { RequestDialog } from '../components/RequestDialog';

export const Home = () => {
  const [isRequestOpen, setIsRequestOpen] = React.useState(false);
  const [activeConfig, setActiveConfig] = React.useState({});
  const [activeContext, setActiveContext] = React.useState("");

  const handleConsultation = (source = "General") => {
    setActiveConfig(globalRequests.consultation);
    setActiveContext(`Home Page - ${source} CTA`);
    setIsRequestOpen(true);
  };

  const getIcon = (iconName, size = 20) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon size={size} className="text-gold" /> : null;
  };
  return (
    <div className="min-h-screen bg-black">
      <RequestDialog
        isOpen={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        config={activeConfig}
        context={activeContext}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gold/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-8 animate-fade-in">
              <Sparkles size={16} className="text-gold" />
              <span className="text-gold text-sm font-medium">Legal Expertise. Exceeding Benchmarks.</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Where <span className="text-gold">Institutional Experience</span> Meets{' '}
              <span className="text-gold">Boutique Agility</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              LEXCEED delivers sophisticated legal counsel on corporate, M&A, and cross-border transactions —
              with the speed, precision, and focus that today's businesses demand.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-16">
              <button
                onClick={() => handleConsultation("Schedule Consultation")}
                className="group px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-semibold rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300 flex items-center"
              >
                Schedule a Consultation
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <Link
                to="/about"
                className="px-8 py-4 border-2 border-gold text-gold font-semibold rounded-lg hover:bg-gold hover:text-black transition-all duration-300"
              >
                Learn More
              </Link>
            </div>

            {/* Attributes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto pt-8">
              {heroAttributes.map((attr, index) => (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-sm border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-105"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  <div className="relative z-10 flex flex-col items-center text-center">
                    <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors duration-300">
                      {getIcon(attr.icon)}
                    </div>
                    <div className="text-white font-serif font-semibold text-lg leading-snug group-hover:text-gold transition-colors duration-300">
                      {attr.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-gold rounded-full animate-scroll"></div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              What <span className="text-gold">We Do</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive legal advisory across the corporate lifecycle—from early-stage ventures to complex cross-border transactions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practiceAreas.slice(0, 6).map((area) => (
              <div
                key={area.id}
                className="group relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-8 hover:border-gold/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                <div className="relative z-10">
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors duration-300">
                    {getIcon(area.icon, 24)}
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold transition-colors duration-300">
                    {area.title}
                  </h3>

                  <p className="text-gray-400 mb-4 leading-relaxed">
                    {area.description}
                  </p>

                  <ul className="space-y-2">
                    {area.highlights.slice(0, 3).map((highlight, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-500">
                        <CheckCircle size={16} className="text-gold mr-2 mt-0.5 flex-shrink-0" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/practice-areas"
              className="inline-flex items-center px-6 py-3 border-2 border-gold text-gold font-semibold rounded-lg hover:bg-gold hover:text-black transition-all duration-300"
            >
              View All Practice Areas
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Highlight */}
      <section className="py-24 bg-black relative overflow-hidden">
        {/* Background Accent */}
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-gold/5 blur-[100px] rounded-full -translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-16 lg:gap-24">

            {/* Subtle Photo Section */}
            <div className="relative order-2 lg:order-1 flex-shrink-0 group">
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-transparent to-transparent blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              <div className="relative overflow-hidden rounded-2xl border border-gold/20 bg-black p-3 backdrop-blur-xl shadow-2xl w-full max-w-[320px] mx-auto lg:mx-0">
                <img
                  src={founderProfile.image}
                  alt={founderProfile.name}
                  className="w-full aspect-[4/5] object-cover object-top rounded-xl transition-all duration-700 ease-in-out"
                />
                <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-gold/30 rounded-tr-xl m-3"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 border-b border-l border-gold/30 rounded-bl-xl m-3"></div>
              </div>


            </div>

            {/* Narrative Section */}
            <div className="order-1 lg:order-2 flex-1 text-center lg:text-left">
              <div className="mb-8">
                <div className="inline-block px-4 py-1 bg-gold/10 border border-gold/30 rounded-full">
                  <span className="text-gold text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em]">Meet the Founding Partner</span>
                </div>
              </div>

              <h2 className="text-4xl lg:text-6xl font-serif font-bold text-white mb-6 tracking-tight leading-tight">
                {founderProfile.name}
              </h2>

              <div className="w-16 h-1 bg-gold mb-8 mx-auto lg:ml-0 opacity-80"></div>

              <p className="text-gray-200 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl font-normal">
                {founderProfile.bio}
              </p>

              {/* Expertise Highlights */}
              <div className="space-y-4 mb-10 max-w-lg mx-auto lg:ml-0">
                {(founderProfile.expertiseHome || founderProfile.expertise).slice(0, 4).map((exp, idx) => (
                  <div key={idx} className="flex items-start text-gray-400 group/item">
                    <CheckCircle size={18} className="text-gold/60 mr-4 mt-1 flex-shrink-0 group-hover/item:text-gold transition-colors" />
                    <span className="text-sm sm:text-base group-hover/item:text-gray-200 transition-colors">{exp}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300"
              >
                Read Full Profile
                <ArrowRight size={24} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
            Ready to <span className="text-gold">Exceed Benchmarks</span>?
          </h2>

          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Let's discuss how LEXCEED can support your next transaction, provide ongoing counsel,
            or help you navigate complex legal challenges with clarity and precision.
          </p>

          <button
            onClick={() => handleConsultation("Get Started Today")}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300"
          >
            Get Started Today
            <ArrowRight size={24} className="ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};
